import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { CommonResponse } from 'models';

export interface WorkerState {
  task_id: string;
  status: string;
  delivery_id: number;
  force_restore: boolean;
  start_time: string;
  progress: string;
  result: any;
  error: string | null;
  worker_thread_id: number;
  elapsed_seconds: number;
  system_info: {
    total_active_tasks: number;
    max_concurrent_tasks: number;
    total_tasks_in_memory: number;
  };
}

const initialState: WorkerState = {
  task_id: '',
  status: '',
  delivery_id: 0,
  force_restore: false,
  start_time: '',
  progress: '',
  result: null,
  error: null,
  worker_thread_id: 0,
  elapsed_seconds: 0,
  system_info: {
    total_active_tasks: 0,
    max_concurrent_tasks: 0,
    total_tasks_in_memory: 0,
  },
};

const workerSlice = createSlice({
  name: 'worker',
  initialState,
  reducers: {
    setWorkerSuccess(state, action: PayloadAction<CommonResponse<WorkerState>>) {
      return { ...state, ...action.payload.data, error: null, loading: false };
    },
    setWorkerFailed(state) {
      state.error = 'Failed to fetch worker status';
    },
    setWorkerProgressing(state, action: PayloadAction<{ delivery_id: number }>) {
      state.delivery_id = action.payload.delivery_id;
      state.status = 'progressing';
      state.progress = 'Initializing restore process...';
      state.start_time = new Date().toISOString();
      state.elapsed_seconds = 0;
      state.error = null;
    },
    getWorker(state) {
      return state;
    },
    updateElapsedSeconds(state, action: PayloadAction<number>) {
      state.elapsed_seconds = Math.round(action.payload);
    },
    setWorkerTaskInfo(state, action: PayloadAction<CommonResponse<WorkerState>>) {
      const data = action.payload.data;
      if (data) {
        // Store task metadata but preserve current status (e.g. 'progressing')
        if (data.task_id) state.task_id = data.task_id;
        if (data.delivery_id) state.delivery_id = data.delivery_id;
        if (data.worker_thread_id) state.worker_thread_id = data.worker_thread_id;
        if (data.system_info) state.system_info = data.system_info;
      }
    },
    resetWorker() {
      return initialState;
    },
  },
});

// Actions
export const workerActions = workerSlice.actions;

// Selectors
export const selectWorker = (state: RootState) => state.worker;
export const selectWorkerTaskId = (state: RootState) => state.worker.task_id;
export const selectWorkerStatus = (state: RootState) => state.worker.status;
export const selectDeliveryIDProgress = (state: RootState) => state.worker.delivery_id;
export const selectElapsedSeconds = (state: RootState) => state.worker.elapsed_seconds;

// Reducer
export const workerReducer = workerSlice.reducer;
